import { Mark, Node, mergeAttributes, Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { formatTimestamp } from "./utils";

export const CaptionBlock = Node.create({
    name: "captionBlock",
    group: "block",
    content: "inline*",
    defining: true,

    addAttributes() {
        return {
            start: { default: 0 },
            end: { default: 0 },
            index: { default: 0 },
        };
    },

    parseHTML() {
        return [{ tag: "div[data-caption-block]" }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(
                {
                    "data-caption-block": "",
                    "data-start": String(node.attrs.start),
                    "data-end": String(node.attrs.end),
                    "data-index": String(node.attrs.index),
                    class: "caption-block",
                },
                HTMLAttributes
            ),
            0,
        ];
    },
});

export const WordMark = Mark.create({
    name: "word",
    inclusive: false,
    spanning: false,

    addAttributes() {
        return {
            confidence: {
                default: null,
                parseHTML: (el) => {
                    const v = el.getAttribute("data-confidence");
                    return v != null ? parseFloat(v) : null;
                },
                renderHTML: (attrs) =>
                    attrs.confidence == null
                        ? {}
                        : { "data-confidence": String(attrs.confidence) },
            },
            bucket: {
                default: "none",
                parseHTML: (el) => el.getAttribute("data-bucket") ?? "none",
                renderHTML: (attrs) => ({
                    "data-bucket": attrs.bucket ?? "none",
                }),
            },
            original: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({}),
            },
            blockIdx: {
                default: null,
                parseHTML: (el) => {
                    const v = el.getAttribute("data-block-idx");
                    return v != null ? parseInt(v, 10) : null;
                },
                renderHTML: (attrs) =>
                    typeof attrs.blockIdx === "number"
                        ? { "data-block-idx": String(attrs.blockIdx) }
                        : {},
            },
            wordIdx: {
                default: null,
                parseHTML: (el) => {
                    const v = el.getAttribute("data-word-idx");
                    return v != null ? parseInt(v, 10) : null;
                },
                renderHTML: (attrs) =>
                    typeof attrs.wordIdx === "number"
                        ? { "data-word-idx": String(attrs.wordIdx) }
                        : {},
            },
        };
    },

    parseHTML() {
        return [{ tag: "span[data-word]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "span",
            mergeAttributes({ "data-word": "" }, HTMLAttributes),
            0,
        ];
    },
});

// Plugin: render a non-editable timestamp chip at the start of each block.
function buildTimestampDecorations(doc: any): DecorationSet {
    const decos: Decoration[] = [];
    doc.descendants((node: any, pos: number) => {
        if (node.type.name !== "captionBlock") return;
        const start =
            typeof node.attrs.start === "number" ? node.attrs.start : 0;
        decos.push(
            Decoration.widget(
                pos + 1,
                () => {
                    const el = document.createElement("span");
                    el.className = "caption-timestamp";
                    el.contentEditable = "false";
                    el.setAttribute("data-timestamp", "");
                    el.textContent = formatTimestamp(start);
                    el.dir = "ltr";
                    return el;
                },
                {
                    side: -1,
                    ignoreSelection: true,
                    key: `ts-${start}`,
                }
            )
        );
        return false;
    });
    return DecorationSet.create(doc, decos);
}

export const TimestampDecoration = Extension.create({
    name: "captionTimestampDecoration",
    addProseMirrorPlugins() {
        const key = new PluginKey<DecorationSet>("captionTimestampDecoration");
        return [
            new Plugin<DecorationSet>({
                key,
                state: {
                    init: (_, state) => buildTimestampDecorations(state.doc),
                    apply(tr, old) {
                        if (!tr.docChanged) return old;
                        // Map existing widget positions; rebuild only if block
                        // structure changed (add / remove / merge / split).
                        const oldBlocks = countBlocks(tr.before);
                        const newBlocks = countBlocks(tr.doc);
                        if (oldBlocks === newBlocks) {
                            return old.map(tr.mapping, tr.doc);
                        }
                        return buildTimestampDecorations(tr.doc);
                    },
                },
                props: {
                    decorations(state) {
                        return key.getState(state);
                    },
                },
            }),
        ];
    },
});

function countBlocks(doc: any): number {
    let n = 0;
    doc.forEach((child: any) => {
        if (child.type.name === "captionBlock") n++;
    });
    return n;
}

// Auto-clear the confidence bucket as soon as the user edits a flagged word
// so the highlight disappears immediately instead of waiting for a save+reload.
export type WordBucketAutoClearOptions = {
    onWordEdited?: (blockIdx: number, wordIdx: number) => void;
};

export const WordBucketAutoClear = Extension.create<WordBucketAutoClearOptions>(
    {
    name: "wordBucketAutoClear",
    addOptions() {
        return { onWordEdited: undefined };
    },
    addProseMirrorPlugins() {
        const options = this.options;
        return [
            new Plugin({
                key: new PluginKey("wordBucketAutoClear"),
                appendTransaction(transactions, _oldState, newState) {
                    if (!transactions.some((t) => t.docChanged)) return null;
                    const wordType = newState.schema.marks.word;
                    if (!wordType) return null;

                    // Collect the union of changed ranges in the NEW doc.
                    const ranges: { from: number; to: number }[] = [];
                    for (const t of transactions) {
                        for (const stepMap of t.mapping.maps) {
                            stepMap.forEach(
                                (
                                    _oldStart: number,
                                    _oldEnd: number,
                                    newStart: number,
                                    newEnd: number
                                ) => {
                                    ranges.push({
                                        from: newStart,
                                        to: newEnd,
                                    });
                                }
                            );
                        }
                    }
                    if (ranges.length === 0) return null;

                    const tr = newState.tr;
                    let modified = false;
                    const seen = new Set<number>();
                    const edited = new Map<string, [number, number]>();
                    const docSize = newState.doc.content.size;

                    for (const { from, to } of ranges) {
                        const scanFrom = Math.max(0, from - 1);
                        const scanTo = Math.min(docSize, to + 1);
                        newState.doc.nodesBetween(
                            scanFrom,
                            scanTo,
                            (node, pos) => {
                                if (!node.isText) return;
                                if (seen.has(pos)) return;
                                seen.add(pos);
                                const mark = node.marks.find(
                                    (m) => m.type === wordType
                                );
                                if (!mark) return;
                                const original = mark.attrs.original;
                                const textChanged =
                                    typeof original === "string" &&
                                    node.text !== original;
                                if (!textChanged) return;

                                const blockIdx = mark.attrs.blockIdx;
                                const wordIdx = mark.attrs.wordIdx;
                                if (
                                    typeof blockIdx === "number" &&
                                    typeof wordIdx === "number"
                                ) {
                                    edited.set(`${blockIdx}:${wordIdx}`, [
                                        blockIdx,
                                        wordIdx,
                                    ]);
                                }

                                if (
                                    mark.attrs.bucket !== "none" ||
                                    mark.attrs.confidence !== 1
                                ) {
                                    const start = pos;
                                    const end = pos + node.nodeSize;
                                    tr.removeMark(start, end, wordType);
                                    tr.addMark(
                                        start,
                                        end,
                                        wordType.create({
                                            ...mark.attrs,
                                            bucket: "none",
                                            confidence: 1,
                                        })
                                    );
                                    modified = true;
                                }
                            }
                        );
                    }

                    if (edited.size > 0 && options.onWordEdited) {
                        const cb = options.onWordEdited;
                        const pairs = Array.from(edited.values());
                        queueMicrotask(() => {
                            for (const [blockIdx, wordIdx] of pairs) {
                                cb(blockIdx, wordIdx);
                            }
                        });
                    }

                    return modified ? tr : null;
                },
            }),
        ];
    },
}
);

// Navigate between low-confidence words via Tab / Shift-Tab.
export type LowConfidenceTarget = { from: number; to: number; bucket: string };

export function findLowConfidencePositions(state: any): LowConfidenceTarget[] {
    const out: LowConfidenceTarget[] = [];
    const wordType = state.schema.marks.word;
    if (!wordType) return out;
    state.doc.descendants((node: any, pos: number) => {
        if (!node.isText) return;
        const mark = node.marks.find(
            (m: any) => m.type === wordType && m.attrs.bucket !== "none"
        );
        if (mark) {
            out.push({
                from: pos,
                to: pos + node.nodeSize,
                bucket: mark.attrs.bucket,
            });
        }
    });
    return out;
}

export const LowConfidenceNavigation = Extension.create({
    name: "lowConfidenceNavigation",
    addKeyboardShortcuts() {
        const jump = (direction: 1 | -1) => {
            const { editor } = this;
            const targets = findLowConfidencePositions(editor.state).sort(
                (a, b) => a.from - b.from
            );
            if (targets.length === 0) return false;
            const cursor = editor.state.selection.from;
            let target: LowConfidenceTarget | undefined;
            if (direction === 1) {
                target = targets.find((t) => t.from > cursor) ?? targets[0];
            } else {
                target =
                    [...targets].reverse().find((t) => t.to < cursor) ??
                    targets[targets.length - 1];
            }
            if (!target) return false;
            const { from, to } = target;
            const tr = editor.state.tr.setSelection(
                TextSelection.create(editor.state.doc, from, to)
            );
            editor.view.dispatch(tr.scrollIntoView());
            editor.view.focus();
            return true;
        };
        return {
            Tab: () => jump(1),
            "Shift-Tab": () => jump(-1),
        };
    },
});

// Split a block proportionally by cursor offset; merge on backspace at block start.
export const BlockSplitMerge = Extension.create({
    name: "captionBlockSplitMerge",
    addKeyboardShortcuts() {
        return {
            Enter: () => {
                const { editor } = this;
                const { state, view } = editor;
                const { $from, empty } = state.selection;
                if (!empty) return false;
                if ($from.parent.type.name !== "captionBlock") return false;

                const block = $from.parent;
                const origStart = Number(block.attrs.start ?? 0);
                const origEnd = Number(block.attrs.end ?? 0);
                const blockContentStart = $from.start();
                const blockContentEnd = $from.end();
                const cursorInBlock = $from.pos - blockContentStart;
                const blockLen = blockContentEnd - blockContentStart;
                const ratio = blockLen > 0 ? cursorInBlock / blockLen : 0;
                const splitTime = origStart + (origEnd - origStart) * ratio;
                const firstBlockPos = $from.before();
                const captionBlockType = state.schema.nodes.captionBlock;
                if (!captionBlockType) return false;

                const tr = state.tr.split($from.pos, 1, [
                    {
                        type: captionBlockType,
                        attrs: {
                            start: splitTime,
                            end: origEnd,
                            index: 0,
                        },
                    },
                ]);

                tr.setNodeMarkup(firstBlockPos, undefined, {
                    ...block.attrs,
                    end: splitTime,
                });

                view.dispatch(tr.scrollIntoView());
                return true;
            },
            Backspace: () => {
                const { editor } = this;
                const { state, view } = editor;
                const { $from, empty } = state.selection;
                if (!empty) return false;
                if ($from.parent.type.name !== "captionBlock") return false;
                if ($from.parentOffset !== 0) return false;

                const blockPos = $from.before();
                if (blockPos === 0) return false;
                const $beforeBlock = state.doc.resolve(blockPos);
                const indexInParent = $beforeBlock.index();
                if (indexInParent === 0) return false;
                const prevBlock = $beforeBlock.parent.child(indexInParent - 1);
                if (prevBlock.type.name !== "captionBlock") return false;

                const currentBlock = $from.parent;
                const mergedStart = Number(prevBlock.attrs.start ?? 0);
                const mergedEnd = Number(currentBlock.attrs.end ?? 0);
                const prevBlockPos = blockPos - prevBlock.nodeSize;

                const tr = state.tr.join(blockPos);
                tr.setNodeMarkup(prevBlockPos, undefined, {
                    ...prevBlock.attrs,
                    start: mergedStart,
                    end: mergedEnd,
                });
                view.dispatch(tr.scrollIntoView());
                return true;
            },
        };
    },
});
